import * as React from 'react';

import {
  Box,
  Button,
  Stack,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import SubmitIcon from '@mui/icons-material/Publish';
import ApproveIcon from '@mui/icons-material/DoneAll';

import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useSnackbar } from 'notistack';

import { yupResolver } from '@hookform/resolvers/yup';
import ControlledForm from '../../components/ControlledForm';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import ScrollToTop from '../../components/ScrollToTop';

import FormReadOnlySection from '../../components/FormReadOnlySection';
import FormPaper from '../../components/FormPaper';
import FormTitle from '../../components/FormTitle';
import FormHeader from '../../components/FormHeader';

import { useAuthorizedFormData } from '../../core/context/AuthorizedFormDataContext';
import { useFlightOperationNotificationData } from '../../core/context/FlightOperationNotificationDataContext';
import { FlightOperationNotification } from '../../core/api/clients';

import FlightStep from './FormSteps/FlightStep';
import EventStep from './FormSteps/EventStep';
import NarrativeStep from './FormSteps/NarrativeStep';

import FlightOperationNotificationVersions from './FlightOperationNotificationVersions';

import '../../core/styles/disabledFields.css';
import FormStatus from '../../components/FormStatus';
import { FormValidationProvider } from '../../core/context/FormValidationContext';
import queryKeys from '../../core/api/queryKeys';

import { useDeleteFlightOperationNotification } from '../../core/api/hooks/flight-operation';
import FlightOperationNotificationValidationSchema from './FlightOperationNotificationValidationSchema';

function FlightOperationNotificationFormView() {
  const [requestedVersionNumber, setRequestedVersionNumber] = React.useState<number | undefined>(undefined);
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const authorizedFormData = useAuthorizedFormData();

  const {
    flightOperationNotification,
    flightOperationNotificationAtVersion,
    flightOperationNotificationVersionGroups,
    beginRequestAtVersion,
    update,
    submit,
    allowedActions,
    submitStatus,
  } = useFlightOperationNotificationData();

  const { mutate: deleteFlightOperationNotificationDraft } = useDeleteFlightOperationNotification();

  const formMethods = useForm<FlightOperationNotification>({
    defaultValues: flightOperationNotification,
    mode: 'onChange',
    resolver: yupResolver(FlightOperationNotificationValidationSchema),
  });

  React.useEffect(() => {
    if (searchParams.has('versionNumber')) {
      const versionString = searchParams.get('versionNumber');
      if (versionString !== null) {
        const versionNumber = Number.parseInt(versionString, 10);
        if (versionNumber !== undefined) {
          setRequestedVersionNumber(versionNumber);
        }
      }
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (requestedVersionNumber === undefined) {
      formMethods.reset(flightOperationNotification);
    } else {
      beginRequestAtVersion(requestedVersionNumber);
    }
  }, [flightOperationNotification, requestedVersionNumber]);

  React.useEffect(() => {
    if (flightOperationNotificationAtVersion !== undefined) {
      formMethods.reset(flightOperationNotificationAtVersion);
    }
  }, [flightOperationNotificationAtVersion]);

  const submitCallback = () => {
    Promise.all([
      queryClient.refetchQueries({ queryKey: [queryKeys.FlightOperationNotification.Drafts] }),
      queryClient.refetchQueries({ queryKey: [queryKeys.FlightOperationNotification.AllSubmissions] }),
    ]).then(() => {
      navigate('/');
    });
  };

  const handleUpdate = () => {
    update(formMethods.getValues());
  };

  const handleSubmit = () => {
    submit(flightOperationNotification.id, submitCallback);
  };

  const handleDelete = () => {
    if (flightOperationNotification.id) {
      deleteFlightOperationNotificationDraft(flightOperationNotification.id, {
        onSuccess: () => {
          queryClient
            .refetchQueries({ queryKey: [queryKeys.FlightOperationNotification.AllSubmissions] })
            .then(() => {
              navigate('/form/flight-operation-notification');
            });
        },
        onError: () => enqueueSnackbar('Error deleting form', { variant: 'error' }),
      });
    }
  };

  const openDeleteConfirmationDialog = () => {
    setConfDialogOpen(true);
  };

  return (
    <FormPaper color={authorizedFormData.metadata?.color}>
      <ScrollToTop />
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleDelete} />
      <FormHeader>
        <FormTitle
          title="Flight Operation Notification"
          subtitle={[[
            'Reported By:',
            flightOperationNotification.employeeName,
            `[${flightOperationNotification.employeeId}],`,
            flightOperationNotification.employeeLocation,
            flightOperationNotification.employeeTitle,
          ].join(' ')]}
        />
        <FormStatus
          status={flightOperationNotification.status}
          subtext={undefined}
        />
        <FlightOperationNotificationVersions
          versionGroups={flightOperationNotificationVersionGroups}
          requestedVersion={requestedVersionNumber}
        />
      </FormHeader>
      <ControlledForm onUpdate={handleUpdate} enableUpdate>
        <FormProvider {...formMethods}>
          <FormValidationProvider<FlightOperationNotification>>
            <Stack spacing={2}>
              <FormReadOnlySection title="Flight & Crew">
                <FlightStep visible readOnly isPriorVersion={requestedVersionNumber !== undefined} />
              </FormReadOnlySection>
              <FormReadOnlySection title="Event">
                <EventStep visible readOnly isPriorVersion={requestedVersionNumber !== undefined} />
              </FormReadOnlySection>
              <FormReadOnlySection title="Narrative">
                <NarrativeStep visible readOnly isPriorVersion={requestedVersionNumber !== undefined} />
              </FormReadOnlySection>
            </Stack>
          </FormValidationProvider>
        </FormProvider>
      </ControlledForm>
      <Box display="flex" sx={{ my: 2 }}>
        <Button
          sx={{ marginRight: 2 }}
          onClick={() => navigate('/form/flight-operation-notification')}
        >
          Close
        </Button>
        {allowedActions?.delete && (
          <Button
            sx={{ marginRight: 2 }}
            color="error"
            variant="outlined"
            onClick={openDeleteConfirmationDialog}
          >
            Delete
          </Button>
        )}
        <Box flexGrow={1} />
        {allowedActions?.submit && requestedVersionNumber === undefined && (
          <LoadingButton
            loading={submitStatus === 'pending'}
            loadingPosition="end"
            endIcon={<SubmitIcon />}
            sx={{ marginRight: 2 }}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={formMethods.formState.isDirty}
          >
            {`${formMethods.formState.isDirty ? 'Submit (Unsaved)' : 'Submit'}`}
          </LoadingButton>
        )}
        {allowedActions?.edit && requestedVersionNumber === undefined && (
          <Button
            sx={{ marginRight: 2 }}
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/form/flight-operation-notification/${flightOperationNotification.id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>
    </FormPaper>
  );
}

export default FlightOperationNotificationFormView;
